import { FC, useMemo } from 'react'
import Link from 'next/link'
import styled from 'styled-components'
import { ArrowRight, Close } from 'svgIcons'
import { useFallbackImageInSSR } from 'utils'
import Button from '@app.components/Button'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import Modal, { IFModalProps } from 'app.components/Modal/Modal'
import { ChannelUserType } from 'app.constants/enums'
import { UserCardItem } from './NewsReactionCard'

export const defaultImage = {
  normal: '/static/images/channel/user-profile.png',
  channel: '/static/images/channel/enterprise.png',
}

interface NewsReactionModalProps extends IFModalProps {
  data: UserCardItem
}

const NewsReactionUserModal: FC<NewsReactionModalProps> = ({
  data,
  onClose,
  ...props
}) => {
  const isWebview = useIsWebview()
  const isChannel = useMemo(() => {
    return data.type === ChannelUserType.CHANNEL
  }, [data])

  const userTitleText = useMemo(() => {
    return data.currentTab === 'scrap'
      ? '최근에 스크랩한 소식'
      : '최근에 좋아요를 누른 소식'
  }, [data])

  const fallbackImageProps = useFallbackImageInSSR(defaultImage[data.type])

  return (
    <Modal isModalOverlapping onClose={onClose} {...props}>
      <StyledWrapper>
        <div className="close-icon">
          <Close onClick={onClose} />
        </div>
        <img
          className="logo-img"
          src={data.imageUrl}
          alt="프로필 사진"
          data-channel={isChannel}
          {...fallbackImageProps}
        />
        {isChannel ? (
          <div className="channel-info">
            <div className="channel-content">
              <div className="user-title ellipsis">{data.name}</div>
              <div className="channel-desc">{data.description}</div>
            </div>
            {/* TODO 채널 상세로 이동 후 스크롤 최상단 앵커 안되는 원인 파악 필요 */}
            <Link href={data.channelUrl + '#top'} replace={!isWebview}>
              <a>
                <Button className="channel-btn">채널 바로가기</Button>
              </a>
            </Link>
          </div>
        ) : (
          <>
            <div className="user-title">{userTitleText}</div>
            <div className="news-list">
              {data.newsItem.map((news) => (
                <Link
                  key={news.id}
                  href={news.newsItemUrl}
                  replace={!isWebview}
                >
                  <a className="news-item" target="_blank">
                    <span className="ellipsis">{news.content}</span>
                    <ArrowRight />
                  </a>
                </Link>
              ))}
            </div>
          </>
        )}
      </StyledWrapper>
    </Modal>
  )
}

export default NewsReactionUserModal

const StyledWrapper = styled.div`
  width: 340px;
  height: 360px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  border-radius: 20px;
  padding: 20px;
  gap: 20px;
  .close-icon {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    svg {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }
  .logo-img {
    width: 94px;
    height: 94px;
    background-color: #eee;
    border-radius: 50%;
    object-fit: cover;
    &[data-channel='true'] {
      border-radius: 17px;
      border: 1px solid #ddd;
    }
  }
  .user-title {
    width: 240px;
    font-size: 20px;
    font-weight: 500;
    text-align: center;
  }
  .channel-info {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 20px;
    .channel-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      .channel-desc {
        width: 240px;
        max-height: 60px;
        font-size: 14px;
        font-weight: 500;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .channel-btn {
      width: 117px;
      height: 45px;
      margin-top: 8px;
    }
  }
  .news-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    .news-item {
      width: 181px;
      height: 38px;
      display: flex;
      align-items: center;
      padding: 0 15px;
      background-color: #f2faff;
      border: 1px solid #add8e6;
      border-radius: 5px;
      span {
        font-size: 12px;
      }
      svg {
        width: 10px;
        height: 10px;
        margin-top: 1px;
      }
    }
  }
  .ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
